import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Namespace } from 'enums-lib';
import { RequestConfigProperties } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { AbstractAuthService } from './auth.service';
import { currentUser } from './current-user';

@Injectable({ providedIn: 'root' })
export class InveniasAuthService extends AbstractAuthService {
  private static readonly INVENIAS_URL_REGEX = new RegExp(/https:\/\/[^.]+\.invenias/); // will also match inveniasnextgen

  constructor(
    identityStorage: IdentityStorage,
    http: HttpClient,
    serviceProvider: SharedServiceProviderServiceFactory,
    private ngZone: NgZone
  ) {
    super(identityStorage, http, serviceProvider);
    console.log('InveniasAuthService created.');
  }

  getRequestProperties(req: HttpRequest<any>, token: string): RequestConfigProperties | undefined {
    if (req.url.match(InveniasAuthService.INVENIAS_URL_REGEX)) {
      return { setHeaders: { Authorization: 'bearer ' + token } };
    }

    return undefined;
  }

  override getTenantId(jwt: string) {
    const decoded = jwtDecode<any>(jwt);
    return decoded["http://www.invenias.com/claims/CompanyCode"];
  }

  getUrlEndpoint(jwt: string) {
    const decoded = jwtDecode<any>(jwt);
    return decoded["http://www.invenias.com/claims/UrlEndpoint"];
  }

  protected getNamespace(): Namespace {
    return Namespace.invenias;
  }

  protected getRequestAuthenticationHostUrl(): string {
    const callbackBase = this.runtimeEnvironment?.runningAsChromeExtension
      ? chrome.identity.getRedirectURL('callback')
      : `${this.environment.uiHost}/callback`;
    const callbackUrl = `${callbackBase}?instance=${this.instanceId}`;
    return `${this.environment.kpHost}/auth/invenias/host?targetCallback=${encodeURIComponent(callbackUrl)}&tenantName=${this.getTenantName()}`;
  }

  protected override redirectToAuthProvider(authUrl: string): void {
    if (this.runtimeEnvironment?.runningAsChromeExtension) {
      this.launchInteractiveAuthFlow(authUrl);
    } else {
      super.redirectToAuthProvider(authUrl);
    }
  }

  private launchInteractiveAuthFlow(authUrl: string): void {
    chrome.identity.launchWebAuthFlow({ url: authUrl, interactive: true }, (responseUrl) => {
      this.ngZone.run(() => {
        if (chrome.runtime.lastError || !responseUrl) {
          console.error('Chrome identity auth flow failed:', chrome.runtime.lastError?.message);
          return;
        }
        this.finishAuthFlow(responseUrl);
        const token = this.identityStorage.get()?.token;
        if (token) {
          this.resolveWithToken(token);
        }
      })
    });
  }

  private getTenantName(): string {
    const tenant = this.identityStorage.get()?.tenant;
    if (!tenant) {
      throw Error('Tenant undefined during authentication.');
    }
    return tenant;
  }

  protected override postTokenAction(jwt: string): void {
    this.setCurrentUser(jwt);
  }

  private setCurrentUser(jwt: string): void {
    const decoded = jwtDecode<any>(jwt);
    const userIdKey = 'http://www.invenias.com/claims/UserId'; // NOTE: could be PersonId
    currentUser.userInfo = {
      id: decoded[userIdKey],
      name: decoded.name,
      email: decoded.email,
    };
  }
}
